import { format as dfFormat, formatDistanceToNow as dfDistanceToNow } from "date-fns";

/**
 * Crash-proof date formatting.
 *
 * date-fns `format()` throws RangeError("Invalid time value") when given an
 * invalid Date. On native iOS this crashed the job screen inside the
 * subscription gate boundary, producing an error storm. Always use this
 * helper for user-facing date rendering.
 */
export function safeFormat(
  value: string | number | Date | null | undefined,
  pattern: string,
  fallback = "—"
): string {
  if (value === null || value === undefined || value === "") return fallback;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  try {
    return dfFormat(date, pattern);
  } catch {
    return fallback;
  }
}

/** Locale-safe date string (en-GB) that never throws. */
export function safeLocaleDate(
  value: string | number | Date | null | undefined,
  fallback = "—",
  options?: Intl.DateTimeFormatOptions
): string {
  if (value === null || value === undefined || value === "") return fallback;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  try {
    return options
      ? date.toLocaleString("en-GB", options)
      : date.toLocaleDateString("en-GB");
  } catch {
    return fallback;
  }
}

/** Intl.DateTimeFormat that never throws on invalid dates. */
export function safeIntlDate(
  value: string | number | Date | null | undefined,
  options: Intl.DateTimeFormatOptions,
  fallback = "—",
  locale = "en-GB"
): string {
  if (value === null || value === undefined || value === "") return fallback;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    return fallback;
  }
}

/**
 * Relative time ("3 hours ago") that never throws.
 *
 * date-fns `formatDistanceToNow()` throws RangeError("Invalid time value") on
 * an unparsable timestamp. Some engines (notably iOS JavaScriptCore) reject
 * timestamp strings that Chrome accepts, which crashed the job detail screen
 * on native and produced a remount/error storm.
 */
export function safeDistanceToNow(
  value: string | number | Date | null | undefined,
  fallback = "—"
): string {
  if (value === null || value === undefined || value === "") return fallback;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  try {
    return dfDistanceToNow(date, { addSuffix: true });
  } catch {
    return fallback;
  }
}
